import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Users, Phone, Mail, Bell, ChevronRight, Search, Filter,
  AlertTriangle, PhoneCall, Briefcase
} from 'lucide-react';
import { AIScoreDisplay } from './AIScoreDisplay';
import { WhatsAppIcon } from './WhatsAppIcon';
import { LEAD_SOURCES } from './constants';

export const LeadsListView = ({ 
  leads, overdueLeads, searchTerm, setSearchTerm, statusFilter, setStatusFilter, 
  sourceFilter, setSourceFilter, showOverdueOnly, setShowOverdueOnly,
  pipelineStages, getStageConfig, getSourceLabel, formatDate,
  openLeadDetails, setActivityDialog, setReminderDialog
}) => {
  const overdueIds = new Set(overdueLeads.map(l => l.id));
  
  return (
    <>
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search by name, phone, email..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {pipelineStages.map(s => (
              <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-48" data-testid="source-filter">
            <SelectValue placeholder="Filter by source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {LEAD_SOURCES.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button 
          variant={showOverdueOnly ? 'destructive' : 'outline'}
          onClick={() => setShowOverdueOnly(!showOverdueOnly)}
          data-testid="overdue-filter-btn"
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          {showOverdueOnly ? 'Showing Overdue' : 'Filter Overdue'}
        </Button>
      </div>

      {/* Leads List */}
      <div className="space-y-2">
        {leads.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No leads found</p>
            </CardContent>
          </Card>
        ) : (
          leads.map(lead => {
            const stageConfig = getStageConfig(lead.status);
            const isOverdue = overdueIds.has(lead.id);
            const overdueLead = overdueLeads.find(o => o.id === lead.id);
            const aiScore = lead.ai_score || 0;
            
            return (
              <Card 
                key={lead.id} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${isOverdue ? 'border-red-300 bg-red-50/30' : ''}`}
                onClick={() => openLeadDetails(lead)}
                data-testid={`lead-card-${lead.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* AI Score */}
                    <div className="hidden sm:block">
                      <AIScoreDisplay score={aiScore} size="md" />
                    </div>
                    
                    {/* Lead Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-lg">{lead.name}</h3>
                        <Badge style={{ backgroundColor: stageConfig.color + '20', color: stageConfig.color }}>
                          {stageConfig.label}
                        </Badge>
                        {isOverdue && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {overdueLead?.days_overdue || 0}d Overdue
                          </Badge>
                        )}
                        <div className="sm:hidden">
                          <AIScoreDisplay score={aiScore} size="sm" />
                        </div>
                      </div>
                      
                      {/* Contact Info Row */}
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {lead.phone}
                        </span>
                        {lead.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {lead.email}
                          </span>
                        )}
                        {(lead.occupation || lead.company) && (
                          <span className="flex items-center gap-1">
                            <Briefcase className="h-3 w-3" /> 
                            {[lead.occupation, lead.company].filter(Boolean).join(' at ')}
                          </span>
                        )}
                      </div>
                      
                      {/* Tags Row */}
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {lead.source && (
                          <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                            {getSourceLabel(lead.source)}
                          </span>
                        )}
                        {lead.interest_area && (
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                            {lead.interest_area}
                          </span>
                        )}
                        {lead.pain_points?.slice(0, 2).map((pp, i) => (
                          <span key={i} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            {pp}
                          </span>
                        ))}
                        {lead.life_events?.slice(0, 1).map((le, i) => (
                          <span key={i} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                            {le}
                          </span>
                        ))}
                      </div>
                      
                      {/* Quick Actions */}
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm"
                          variant="outline"
                          className="h-8 px-3 bg-green-50 hover:bg-green-100 border-green-200"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            window.open(`tel:${lead.phone}`, '_self');
                          }}
                          data-testid={`call-btn-${lead.id}`}
                        >
                          <Phone className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="h-8 px-3 bg-emerald-50 hover:bg-emerald-100 border-emerald-200"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            window.open(`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`, '_blank');
                          }}
                          data-testid={`whatsapp-btn-${lead.id}`}
                        >
                          <WhatsAppIcon className="h-4 w-4 text-emerald-600" />
                        </Button>
                        {lead.email && (
                          <Button 
                            size="sm"
                            variant="outline"
                            className="h-8 px-3 bg-blue-50 hover:bg-blue-100 border-blue-200"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              window.open(`mailto:${lead.email}`, '_self');
                            }}
                            data-testid={`email-btn-${lead.id}`}
                          >
                            <Mail className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                        <div className="flex-1" />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setActivityDialog({ open: true, leadId: lead.id }); }}
                          data-testid={`log-activity-btn-${lead.id}`}
                        >
                          <PhoneCall className="h-4 w-4 mr-1" />
                          Log
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => { e.stopPropagation(); setReminderDialog({ open: true, leadId: lead.id }); }}
                          data-testid={`reminder-btn-${lead.id}`}
                        >
                          <Bell className="h-4 w-4" />
                        </Button>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </>
  );
};
